import type { CaiConfig, ScannerBrief } from "../types.js";
import { scanProjectModel } from "./manifest.js";
import { scanEntryPoints } from "./entry-points.js";
import { scanServiceGraph } from "./service-graph.js";
import { scanReconciliation } from "./reconciliation.js";
import { scanFolderTree } from "./folder-tree.js";
import { scanTooling } from "./tooling.js";
import { scanReadme } from "./readme.js";

/** Run pre-analysis scan and return brief or prompt */
export async function runScan(
  config: CaiConfig,
  opts: { jsonOnly?: boolean }
): Promise<ScannerBrief | string> {
  const brief = buildBrief(config.projectRoot);

  if (opts.jsonOnly) return brief;

  return buildPrompt(brief);
}

/** Build the scanner brief from codebase analysis */
function buildBrief(projectRoot: string): ScannerBrief {
  const safe = <T>(label: string, fn: () => T, fallback: T): T => {
    try {
      return fn();
    } catch (err) {
      process.stderr.write(`Warning: scanner '${label}' failed: ${(err as Error).message}\n`);
      return fallback;
    }
  };

  const project = safe("manifest", () => scanProjectModel(projectRoot), {
    rootManifest: null, workspaces: [], workspaceDependencies: [], commands: [],
  });
  const entryPoints = safe("entry-points", () => scanEntryPoints(projectRoot), []);
  const readme = safe("readme", () => scanReadme(projectRoot), null);

  return {
    schemaVersion: "1.0",
    manifest: project.rootManifest,
    project,
    entryPoints,
    serviceGraph: safe("service-graph", () => scanServiceGraph(projectRoot, entryPoints), { nodes: [], edges: [] }),
    reconciliation: safe("reconciliation", () => scanReconciliation(readme, project), { documentedCommands: [], undocumentedCommands: [], documentedWorkspaces: [], undocumentedWorkspaces: [] }),
    folderTree: safe("folder-tree", () => scanFolderTree(projectRoot), []),
    tooling: safe("tooling", () => scanTooling(projectRoot), { testRunner: null, buildTool: null, linter: null, formatter: null, packageManager: null }),
    readme,
    timestamp: new Date().toISOString(),
  };
}

const README_MAX_CHARS = 3_000;

/** Build AI prompt with embedded brief */
function buildPrompt(brief: ScannerBrief): string {
  // Cap readme to avoid bloating the prompt — full content rarely needed for scaffold init
  const capped = brief.readme && brief.readme.length > README_MAX_CHARS
    ? { ...brief, readme: brief.readme.slice(0, README_MAX_CHARS) + "\n… [truncated]" }
    : brief;
  const briefJson = JSON.stringify(capped);

  return `Here is a pre-analyzed brief of the codebase — do NOT explore the filesystem yourself, reason from this brief:

<brief>
${briefJson}
</brief>

Using this brief, populate the CAI · Coherence AI scaffold files. Focus on:
1. context/architecture.md — system components, data flow, integrations
2. context/stack.md — technologies, versions, key libraries
3. context/conventions.md — code patterns, naming, file organization
4. context/decisions.md — architectural choices and their rationale
5. context/setup.md — how to set up and run the project
6. ROUTER.md — update the "Current Project State" section

For each file, use the information from the brief rather than exploring the filesystem.
Be precise about versions, paths, dependencies, workspace-specific commands, workspace relationships, service/import relationships, and README gaps surfaced in reconciliation — they come directly from the project model and scanner graph.`;
}
